import React, { useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Row, Col, Card, List, Avatar, Flex, Dropdown, Spin, notification, Button, Tooltip } from "antd"
import {
  UserOutlined,
  ShareAltOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
  FileTextOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons"
import WaiverUploaded from "../../../../../assets/icons/waiver-uploaded.svg"
import WaiverNotSigned from "../../../../../assets/icons/waiver-not-signed.svg"
import Questionnaire from "../../../../../assets/icons/questionnaire.svg"
import styles from "../../index.module.css"
import { GreenCheckIcon, RemoveQuestionnaireIcon } from "icons"
import { WaiverDetailsModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/WaiverDetails"
import CustomQuestions from "pagesV2/pro/Embedded/BookingConfirmation/modal/CustomQuestions"
import DeleteConfirmation from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/Deleteconfirmation"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { deleteSharedWaiver, getWaiverSubmissionsByTripBooking, notifyWaiver } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import NotifyIcon from "../../../../../assets/icons/new/filters/Notify.svg"
import { NotifyModal } from "../../modal/Notify"

const StatusBox = React.memo(
  ({
    containerClassName,
    boxClassName,
    isCompleted,
    title,
    statusText,
    showContent = true,
    completedIconSrc,
    incompleteIconSrc,
    completedIconAlt,
    incompleteIconAlt,
    clickable,
    onClick
  }) => {
    const stateClassName = isCompleted ? "Completed" : "Not-completed"
    const classes = `${stateClassName} commonBox` + (boxClassName ? ` ${boxClassName}` : "")

    const content = (
      <div className={classes}>
        {showContent &&
          (isCompleted ? (
            <div className="relative">
              <img src={completedIconSrc} alt={completedIconAlt} />
              <GreenCheckIcon className="absolute" />
            </div>
          ) : (
            <div className="relative">
              <img src={incompleteIconSrc} alt={incompleteIconAlt} />
              <RemoveQuestionnaireIcon className="absolute" />
            </div>
          ))}

        {showContent && (
          <Flex vertical>
            <Text fw={500}>{title}</Text>
            <Text>{statusText}</Text>
          </Flex>
        )}
      </div>
    )

    if (clickable) {
      return (
        <button
          type="button"
          className={containerClassName}
          onClick={onClick}
          style={{ cursor: "pointer", border: 0, padding: 0, background: "transparent", textAlign: "inherit" }}
        >
          {content}
        </button>
      )
    }

    return <div className={containerClassName}>{content}</div>
  }
)

StatusBox.propTypes = {
  containerClassName: PropTypes.string,
  boxClassName: PropTypes.string,
  isCompleted: PropTypes.bool,
  title: PropTypes.node,
  statusText: PropTypes.node,
  showContent: PropTypes.bool,
  completedIconSrc: PropTypes.string,
  incompleteIconSrc: PropTypes.string,
  completedIconAlt: PropTypes.string,
  incompleteIconAlt: PropTypes.string,
  clickable: PropTypes.bool,
  onClick: PropTypes.func
}

StatusBox.defaultProps = {
  containerClassName: "",
  boxClassName: "",
  isCompleted: false,
  title: "",
  statusText: "",
  showContent: true,
  completedIconSrc: "",
  incompleteIconSrc: "",
  completedIconAlt: "completed",
  incompleteIconAlt: "incomplete",
  clickable: false,
  onClick: undefined
}

const ActionCards = React.memo(({ inviteWaiverDisabled, inviteWaiverTooltip, signWaiverDisabled, signWaiverTooltip, onInviteWaiver, onSignChildWaiver }) => (
  <Row gutter={[16, 16]} className="mb-xl">
    <Col xs={24} sm={12}>
      <Tooltip title={inviteWaiverDisabled ? inviteWaiverTooltip : ""}>
        <Card
          className={`${styles.waiverCard} ${inviteWaiverDisabled ? styles.disabledCard : "cursor-pointer"}`}
          onClick={inviteWaiverDisabled ? undefined : onInviteWaiver}
          style={{ opacity: inviteWaiverDisabled ? 0.5 : 1, cursor: inviteWaiverDisabled ? "not-allowed" : "pointer" }}
        >
          <Flex vertical align="center" gap={4}>
            <ShareAltOutlined className={`${styles.waiverCardIcon} text-color-primary mb-xxs`} />
            <Text className={`${styles.waiverCardTitle} text-color-primary`}>Invite to sign waiver</Text>
            <Text className={`${styles.waiverCardRequired} color-text-secondary-dark`}>(required)</Text>
          </Flex>
        </Card>
      </Tooltip>
    </Col>

    <Col xs={24} sm={12}>
      <Tooltip title={signWaiverDisabled ? signWaiverTooltip : ""}>
        <Card
          className={`${styles.waiverCard} ${signWaiverDisabled ? styles.disabledCard : "cursor-pointer"}`}
          onClick={signWaiverDisabled ? undefined : onSignChildWaiver}
          style={{ opacity: signWaiverDisabled ? 0.5 : 1, cursor: signWaiverDisabled ? "not-allowed" : "pointer" }}
        >
          <Flex vertical align="center" gap={4}>
            <EditOutlined className={`${styles.waiverCardIcon} text-color-primary`} />
            <Text className={`${styles.waiverCardTitle} text-color-primary`}>Sign waiver</Text>
            <Text className={`${styles.waiverCardRequired} color-text-secondary-dark`}>(required)</Text>
          </Flex>
        </Card>
      </Tooltip>
    </Col>
  </Row>
))

ActionCards.propTypes = {
  inviteWaiverDisabled: PropTypes.bool,
  inviteWaiverTooltip: PropTypes.string,
  signWaiverDisabled: PropTypes.bool,
  signWaiverTooltip: PropTypes.string,
  onInviteWaiver: PropTypes.func,
  onSignChildWaiver: PropTypes.func
}

ActionCards.defaultProps = {
  inviteWaiverDisabled: false,
  inviteWaiverTooltip: "",
  signWaiverDisabled: false,
  signWaiverTooltip: "",
  onInviteWaiver: undefined,
  onSignChildWaiver: undefined
}

const GuestRow = React.memo(
  ({ guest, index, menuItems, onMenuClick, bookingQuestionnaireId, bookingQuestionnaireStatus, onOpenQuestionnaire }) => {
    const effectiveQId = guest?.booking_questionnaire?.id || (index === 0 ? bookingQuestionnaireId : null)
    const canOpenQuestionnaire = Boolean(effectiveQId && index === 0)
    const questionnaireStatus = guest?.booking_questionnaire?.status || bookingQuestionnaireStatus
    const isQuestionnaireSubmitted = questionnaireStatus === "submitted"
    const isWaiverSigned = guest.status === "Signed"
    
    let waiverStatusText
    if (guest.total_documents > 0) {
      waiverStatusText = `${guest.total_documents - guest.pending_documents}/${guest.total_documents} Signed`
    } else if (isWaiverSigned) {
      waiverStatusText = "Signed"
    } else {
      waiverStatusText = "Not signed"
    }

    return (
      <List.Item
        className={styles.guestItem}
        actions={[
          <Dropdown
            key="dropdown"
            menu={{
              items: menuItems,
              onClick: (e) => onMenuClick(e, guest)
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<MoreOutlined className={styles.moreIcon} />}
              aria-label={`Guest actions for ${guest?.name || "guest"}`}
            />
          </Dropdown>
        ]}
      >
        <List.Item.Meta
          className={styles.guestItemMeta}
          avatar={<Avatar icon={<UserOutlined />} />}
          title={
            <Text className={`${styles.guestName} fw-500`}>
              {guest.name}
              {guest.participant_type === "Primary Guest" ? " (Primary Guest)" : ""}
            </Text>
          }
          description={<Text className={`${styles.guestType}`}>{guest.participant_type || "Child"}</Text>}
        />

        <div className={styles.guestStatusGroup}>
          <StatusBox
            containerClassName={styles.questionnaireStatusBox}
            boxClassName="questionnaire"
            isCompleted={isQuestionnaireSubmitted}
            title="Questionnaire"
            statusText={isQuestionnaireSubmitted ? "Completed" : "Not completed"}
            showContent={canOpenQuestionnaire}
            completedIconSrc={Questionnaire}
            incompleteIconSrc={WaiverNotSigned}
            completedIconAlt="questionnaire_submitted"
            incompleteIconAlt="questionnaire_not_submitted"
            clickable={canOpenQuestionnaire}
            onClick={() => onOpenQuestionnaire(guest, effectiveQId)}
          />

          <StatusBox
            containerClassName={styles.waiverStatusBox}
            isCompleted={isWaiverSigned}
            title="Waiver"
            statusText={waiverStatusText}
            showContent
            completedIconSrc={WaiverUploaded}
            incompleteIconSrc={WaiverNotSigned}
            completedIconAlt="waiver_uploaded"
            incompleteIconAlt="waiver_not_signed"
          />
        </div>
      </List.Item>
    )
  }
)

GuestRow.propTypes = {
  guest: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    status: PropTypes.string,
    participant_type: PropTypes.string,
    total_documents: PropTypes.number,
    pending_documents: PropTypes.number,
    pending_submission: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    shared: PropTypes.bool,
    shared_waiver_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    questionnaire_editable: PropTypes.bool,
    notify_details: PropTypes.shape({
      email: PropTypes.string,
      phone_number: PropTypes.string
    }),
    booking_questionnaire: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string
    })
  }),
  index: PropTypes.number,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      icon: PropTypes.node,
      label: PropTypes.node,
      type: PropTypes.string,
      danger: PropTypes.bool
    })
  ).isRequired,
  onMenuClick: PropTypes.func,
  bookingQuestionnaireId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingQuestionnaireStatus: PropTypes.string,
  onOpenQuestionnaire: PropTypes.func
}

GuestRow.defaultProps = {
  guest: {},
  index: 0,
  onMenuClick: undefined,
  bookingQuestionnaireId: null,
  bookingQuestionnaireStatus: null,
  onOpenQuestionnaire: undefined
}

const MembersCard = React.memo(
  ({
    isSharedWaiverAllowed,
    setTotalDocumentCount,
    setTotalDocuments,
    setPendingWaiverSubmission,
    setIsShowSignForChildWaiver,
    uuid,
    isShowGuestSubmission,
    booking_questionnaire,
    bookingQuestionnaireId,
    guests = [],
    loading,
    onInviteWaiver,
    onSignChildWaiver
  }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [showCustomQuestionsModal, setShowCustomQuestionsModal] = useState(false)
    const [isShowWaiverDetailsModal, setIsShowWaiverDetailsModal] = useState(false)
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
    const [isShowLoading, setIsShowLoading] = useState(false)
    const [record, setRecord] = useState(null)
    const [questionnaireId, setQuestionnaireId] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleteRecord, setDeleteRecord] = useState(null)
    const [questionnaireEditable, setQuestionnaireEditable] = useState(false)
    const [isShowNotifyModal, setIsShowNotifyModal] = useState(false)
    const [currentNotifyGuest, setCurrentNotifyGuest] = useState(null)
    const { loading: deleteLoading } = useSelector((state) => state.digitalWaiver.sharedWaivers)
    const { loading: notifyLoading } = useSelector((state) => state.digitalWaiver.notifyWaiver) || {}
    const bookingWaivers = useSelector((state) => state.bookings.booking?.booking_waivers) || []

    // When every attached waiver is primary-only and already signed, the primary guest's signature
    // covers the whole booking — nobody else can sign, so the "Sign waiver" action is disabled.
    const isPrimaryGuestOnly = bookingWaivers.length > 0 && !bookingWaivers.some((bookingWaiver) => bookingWaiver?.sign_by === "all")
    const isPrimaryWaiverAlreadySigned = isPrimaryGuestOnly && bookingWaivers.every((bookingWaiver) => bookingWaiver?.is_signed)
    const signWaiverDisabledReason = isPrimaryWaiverAlreadySigned ? "The primary guest has already signed this waiver." : ""

    const requiredActionsCount = useMemo(
      () => (isSharedWaiverAllowed ? 1 : 0) + (isShowGuestSubmission ? 0 : 1),
      [isSharedWaiverAllowed, isShowGuestSubmission]
    )
    const inviteWaiverDisabled = !isSharedWaiverAllowed || isPrimaryWaiverAlreadySigned
    const signWaiverDisabled = isShowGuestSubmission || isPrimaryWaiverAlreadySigned

    const refreshWaiverSubmissions = useCallback(() => {
      dispatch(getWaiverSubmissionsByTripBooking(uuid))
    }, [dispatch, uuid])

    // Guard the actions at the logic level so re-enabling a button via dev tools is a no-op
    const handleSignWaiver = useCallback(() => {
      if (signWaiverDisabled) return
      onSignChildWaiver?.()
    }, [signWaiverDisabled, onSignChildWaiver])

    const handleInviteWaiver = useCallback(() => {
      if (inviteWaiverDisabled) return
      onInviteWaiver?.()
    }, [inviteWaiverDisabled, onInviteWaiver])

    const openQuestionnaire = useCallback(
      (guest, fallbackQuestionnaireId = bookingQuestionnaireId) => {
        setShowCustomQuestionsModal(true)
        setQuestionnaireId(guest?.booking_questionnaire?.id || fallbackQuestionnaireId)
        setQuestionnaireEditable(guest?.questionnaire_editable)
      },
      [bookingQuestionnaireId]
    )

    const handleMenuClick = useCallback(
      (e, guest) => {
        switch (e.key) {
          case "open-waiver":
            setIsShowWaiverDetailsModal(true)
            setIsShowLoading(true)
            setRecord(guest)
            break
          case "open-questionnaire":
            openQuestionnaire(guest)
            break
          case "sign-for-child":
            // Guard against the disabled state being bypassed (e.g. via dev tools)
            if (isPrimaryWaiverAlreadySigned) break
            setIsShowSignForChildWaiver(true)
            setPendingWaiverSubmission(guest?.pending_submission)
            setTotalDocumentCount(guest?.total_documents - guest?.pending_documents)
            break
          case "sign-waiver":
            // Guard against the disabled state being bypassed (e.g. via dev tools)
            if (isPrimaryWaiverAlreadySigned) break
            onSignChildWaiver?.()
            break
          case "remove-guest":
            setShowDeleteModal(true)
            setDeleteRecord(guest)

            break
          case "notify":
            setCurrentNotifyGuest(guest)
            setIsShowNotifyModal(true)
            break
          default:
            break
        }
      },
      [openQuestionnaire, setIsShowSignForChildWaiver, setPendingWaiverSubmission, setTotalDocumentCount, onSignChildWaiver, isPrimaryWaiverAlreadySigned]
    )

    const getMenuItems = useCallback(
      (guest, index) => {
        const items = []

        if (guest.status === "Signed") {
          items.push({
            key: "open-waiver",
            icon: <FileTextOutlined />,
            label: "Open waiver"
          })
        } else if (guest.participant_type === "Child") {
          items.push({
            key: "sign-for-child",
            icon: <FileTextOutlined />,
            label: isPrimaryWaiverAlreadySigned ? (
              <Tooltip title={signWaiverDisabledReason}>
                <span style={{ display: "inline-block", pointerEvents: "auto" }}>Sign for child</span>
              </Tooltip>
            ) : (
              "Sign for child"
            ),
            disabled: isPrimaryWaiverAlreadySigned
          })
        } else {
          items.push({
            key: "sign-waiver",
            icon: <EditOutlined />,
            label: isPrimaryWaiverAlreadySigned ? (
              <Tooltip title={signWaiverDisabledReason}>
                <span style={{ display: "inline-block", pointerEvents: "auto" }}>Sign waiver</span>
              </Tooltip>
            ) : (
              "Sign waiver"
            ),
            disabled: isPrimaryWaiverAlreadySigned
          })
        }

        const effectiveQuestionnaireId = guest?.booking_questionnaire?.id || (index === 0 ? bookingQuestionnaireId : null)
        if (effectiveQuestionnaireId && index === 0) {
          items.push({
            key: "open-questionnaire",
            icon: guest?.booking_questionnaire?.status === "submitted" ? <EditOutlined /> : <QuestionCircleOutlined />,
            label: guest?.booking_questionnaire?.status === "submitted" ? "Edit Questionnaire" : "Open questionnaire"
          })
        }
        // We can only act on a shared guest once we have their shared record.
        // shared_waiver_id persists after the guest signs, unlike guest.shared.
        const hasGuestDetails = Boolean(guest?.shared_waiver_id)
        const isNotSigned = guest?.status !== "Signed"
        const hasNotifyDetails = Boolean(guest?.notify_details?.email || guest?.notify_details?.phone_number)

        // Notify only makes sense before the guest has signed.
        if (hasGuestDetails && isNotSigned && hasNotifyDetails) {
          items.push({
            key: "notify",
            label: "Notify Guest",
            icon: <img src={NotifyIcon} alt="notify" style={{ width: 16, height: 16 }} />
          })
        }

        // Remove stays available whether or not the guest has signed.
        if (hasGuestDetails) {
          items.push(
            {
              type: "divider"
            },
            {
              key: "remove-guest",
              icon: <DeleteOutlined />,
              label: "Remove guest",
              danger: true
            }
          )
        }

        return items
      },
      [bookingQuestionnaireId, isPrimaryWaiverAlreadySigned, signWaiverDisabledReason]
    )

    const handleDeleteCancel = useCallback(() => {
      setShowDeleteModal(false)
    }, [])

    const handleDeleteConfirm = useCallback(() => {
      if (!deleteRecord?.shared_waiver_id) return

      dispatch(deleteSharedWaiver(deleteRecord.shared_waiver_id)).then((res) => {
        toaster.success(res?.message)
        refreshWaiverSubmissions()
        setShowDeleteModal(false)
        setDeleteRecord(null)
      })
    }, [deleteRecord?.shared_waiver_id, dispatch, refreshWaiverSubmissions])

    const closeNotifyModal = useCallback(() => {
      setIsShowNotifyModal(false)
      setCurrentNotifyGuest(null)
    }, [])

    const handleNotifySend = useCallback(
      (values) => {
        if (!currentNotifyGuest?.shared_waiver_id) return

        const payload = {
          shared_waiver_id: currentNotifyGuest.shared_waiver_id,
          notify_by: values.sendMethod
        }

        if (values.sendMethod === "email") {
          payload.email = values.email
        } else {
          payload.phone_number = values.phoneNumber?.fullNumber
        }

        dispatch(notifyWaiver(uuid, payload)).then((res) => {
          notification.success({
            message: "Notification Sent",
            description: res?.message || "Guest has been notified successfully.",
            placement: "topRight"
          })
          refreshWaiverSubmissions()
          closeNotifyModal()
        })
      },
      [closeNotifyModal, currentNotifyGuest, dispatch, refreshWaiverSubmissions, uuid]
    )

    return (
      <>
        <Card className={styles.mainCard} title="Members">
          <Text className="mb-3xl block">A few easy steps: sign the waiver, answer a few questions, and you're all set to go.</Text>
          <Text className={`${styles.sectionTitle}`}>Required Actions ({requiredActionsCount})</Text>
          <div className={`${styles.infoAlert} my-m`}>
            <span className={styles.infoIcon}>
              <InfoCircleOutlined />
            </span>
            <Text>{t("digital_waiver_and_contracts.disclaimer_for_waiver")}</Text>
          </div>
          <ActionCards
            inviteWaiverDisabled={inviteWaiverDisabled}
            inviteWaiverTooltip={signWaiverDisabledReason}
            signWaiverDisabled={signWaiverDisabled}
            signWaiverTooltip={signWaiverDisabledReason}
            onInviteWaiver={handleInviteWaiver}
            onSignChildWaiver={handleSignWaiver}
          />
          <Flex className="mt-3xl mb-m" justify="space-between" align="center" gap={10}>
            <Text className={styles.sectionTitle}>Guests ({guests.length})</Text>
          </Flex>
          <Spin spinning={loading} style={{ width: "100%" }}>
            <div className={styles.guestListContainer}>
              <List
                className={styles.guestList}
                itemLayout="horizontal"
                dataSource={guests}
                renderItem={(guest, index) => (
                  <GuestRow
                    key={guest?.id || guest?.shared_waiver_id || `${guest?.name || "guest"}-${index}`}
                    guest={guest}
                    index={index}
                    menuItems={getMenuItems(guest, index)}
                    onMenuClick={handleMenuClick}
                    bookingQuestionnaireId={bookingQuestionnaireId}
                    bookingQuestionnaireStatus={booking_questionnaire?.status}
                    onOpenQuestionnaire={openQuestionnaire}
                  />
                )}
              />
            </div>
          </Spin>
        </Card>
        {showCustomQuestionsModal && (
          <CustomQuestions
            questionnaireEditable={questionnaireEditable}
            uuid={uuid}
            bookingQuestionnaireId={questionnaireId}
            visible={showCustomQuestionsModal}
            onClose={() => setShowCustomQuestionsModal(false)}
          />
        )}

        {isShowWaiverDetailsModal && (
          <WaiverDetailsModal
            uuid={uuid}
            isShowLoading={isShowLoading}
            setIsShowLoading={setIsShowLoading}
            currentDocumentIndex={currentDocumentIndex}
            setCurrentDocumentIndex={setCurrentDocumentIndex}
            record={record}
            open={isShowWaiverDetailsModal}
            onCancel={() => {
              setIsShowWaiverDetailsModal(false)
              setCurrentDocumentIndex(0)
            }}
          />
        )}
        {showDeleteModal && (
          <DeleteConfirmation
            title={"Remove Guest?"}
            // content='Are you sure you want to remove this guest from the trip? This action cannot be undone.'
            content={
              <span>
                {t("digital_waiver_and_contracts.delete_confirmation")} <strong>{deleteRecord?.name}?</strong>{" "}
                {t("digital_waiver_and_contracts.delete_confirmation_suffix")}
              </span>
            }
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            confirmText="Remove"
            // record={deleteRecord}
            loading={deleteLoading}
          />
        )}
        {isShowNotifyModal && (
          <NotifyModal
            visible={isShowNotifyModal}
            loading={notifyLoading}
            guestData={currentNotifyGuest}
            onClose={() => {
              closeNotifyModal()
            }}
            onSend={handleNotifySend}
          />
        )}
      </>
    )
  }
)

MembersCard.propTypes = {
  setTotalDocumentCount: PropTypes.func,
  setTotalDocuments: PropTypes.func,
  setPendingWaiverSubmission: PropTypes.func,
  guests: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      waiver: PropTypes.string,
      questionnaire: PropTypes.string,
      main: PropTypes.bool
    })
  ),
  onInviteWaiver: PropTypes.func,
  onSignChildWaiver: PropTypes.func,
  setIsShowSignForChildWaiver: PropTypes.func,
  uuid: PropTypes.string,
  isShowGuestSubmission: PropTypes.bool,
  booking_questionnaire: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string
  }),
  bookingQuestionnaireId: PropTypes.number,
  loading: PropTypes.bool,
  isSharedWaiverAllowed: PropTypes.bool
}

MembersCard.displayName = "MembersCard"

export default MembersCard
